import { useMemo, useState } from 'react';
import { Debt, Goal, Transaction } from '../types';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, subMonths, addMonths, startOfMonth } from 'date-fns';

interface ProjectionChartProps {
  transactions: Transaction[];
  debts: Debt[];
  goals: Goal[];
}

type ViewType = 'debt' | 'savings';

// Every debt (mortgage or loan) can be set to a non-monthly payment
// schedule (bi-weekly, weekly, semi-monthly), in which case
// monthlyMinimum holds the per-payment amount, not a monthly figure —
// used as-is below without this, a bi-weekly mortgage payment (roughly
// half its true monthly-equivalent) understates the real payment,
// which can even make the simulated balance grow instead of shrink.
function getMonthlyAmount(amount: number, frequency: string | undefined): number {
  if (!frequency || frequency === 'monthly') return amount;
  if (frequency === 'semi-monthly') return amount * 2;
  if (frequency === 'bi-weekly') return amount * (26 / 12);
  if (frequency === 'weekly') return amount * (52 / 12);
  return amount;
}

export default function ProjectionChart({ transactions, debts, goals }: ProjectionChartProps) {
  const [view, setView] = useState<ViewType>('debt');

  const data = useMemo(() => {
    const today = new Date();

    const currentTotalDebt = debts.reduce((sum, d) => sum + (d.currentAmount || 0), 0);
    const currentTotalSavings = goals.reduce((sum, g) => sum + (g.currentAmount || 0), 0);

    let historicalDebt = currentTotalDebt;
    let historicalSavings = currentTotalSavings;

    const pastData = [];
    for (let i = 0; i <= 6; i++) {
      const targetDate = subMonths(today, i);
      const monthStr = format(targetDate, 'MMM yyyy');
      
      let paymentsThatMonth = 0;
      let savingsThatMonth = 0;
      
      transactions.forEach(tx => {
        if (!tx.date) return;
        const txDate = new Date(tx.date);
        if (txDate.getMonth() === targetDate.getMonth() && txDate.getFullYear() === targetDate.getFullYear()) {
          if (tx.type === 'debt_payment') paymentsThatMonth += tx.amount;
          if (tx.type === 'savings_contribution') savingsThatMonth += tx.amount;
        }
      });

      pastData.unshift({
        month: monthStr,
        actualDebt: historicalDebt,
        actualSavings: historicalSavings,
        projectedDebt: historicalDebt,
        projectedSavings: historicalSavings,
      });

      historicalDebt = historicalDebt + paymentsThatMonth;
      historicalSavings = Math.max(0, historicalSavings - savingsThatMonth);
    }
    
    const futureData = [];
    
    let simDebts = debts.map(d => ({ ...d }));
    
    let totalRecentSavings = 0;
    const threeMonthsAgo = startOfMonth(subMonths(today, 3));
    transactions.forEach(tx => {
      if (!tx.date) return;
      const txDate = new Date(tx.date);
      if (txDate >= threeMonthsAgo && tx.type === 'savings_contribution') {
        totalRecentSavings += tx.amount;
      }
    });
    // Average monthly savings over the last 3 months, default to 100 if no history to show *some* projection
    const avgMonthlySavings = Math.max(100, totalRecentSavings / 3); 
    
    let simSavings = currentTotalSavings;

    for (let i = 1; i <= 12; i++) {
      const targetDate = addMonths(today, i);
      const monthStr = format(targetDate, 'MMM yyyy');

      let monthTotalDebt = 0;
      simDebts.forEach(d => {
        if (d.currentAmount > 0) {
          const interest = d.currentAmount * ((d.interestRate || 0) / 100) / 12;
          const monthlyPayment = getMonthlyAmount(d.monthlyMinimum || 0, d.schedule?.frequency);
          d.currentAmount = Math.max(0, d.currentAmount + interest - monthlyPayment);
        }
        monthTotalDebt += d.currentAmount;
      });

      simSavings += avgMonthlySavings;

      futureData.push({
        month: monthStr,
        actualDebt: null,
        actualSavings: null,
        projectedDebt: monthTotalDebt,
        projectedSavings: simSavings
      });
    }

    return [...pastData, ...futureData];

  }, [transactions, debts, goals]);

  return (
    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col w-full mt-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h3 className="font-bold text-slate-800 text-lg">Financial Trajectory</h3>
          <p className="text-sm text-slate-500">Projected mathematical path based on your current payments & savings rate.</p>
        </div>
        
        <div className="flex bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => setView('debt')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'debt' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Debt Paydown
          </button>
          <button
            onClick={() => setView('savings')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'savings' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Savings Growth
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-[350px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorActualDebt" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorProjectedDebt" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#fca5a5" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#fca5a5" stopOpacity={0}/>
              </linearGradient>
              
              <linearGradient id="colorActualSavings" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorProjectedSavings" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6ee7b7" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#6ee7b7" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis 
              dataKey="month" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 'bold' }} 
              minTickGap={30}
            />
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 'bold' }} 
              tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`} 
            />
            <Tooltip 
              cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              formatter={(value: number, name: string) => [`$${value.toFixed(2)}`, name.replace(/([A-Z])/g, ' $1').trim()]}
            />
            <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b' }} />
            
            {view === 'debt' ? (
              <>
                <Area 
                  type="monotone" 
                  dataKey="projectedDebt" 
                  name="Projected Debt" 
                  stroke="#fca5a5" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  fillOpacity={1} 
                  fill="url(#colorProjectedDebt)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="actualDebt" 
                  name="Actual Debt" 
                  stroke="#e11d48" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorActualDebt)" 
                  connectNulls={false}
                />
              </>
            ) : (
              <>
                <Area 
                  type="monotone" 
                  dataKey="projectedSavings" 
                  name="Projected Savings" 
                  stroke="#6ee7b7" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  fillOpacity={1} 
                  fill="url(#colorProjectedSavings)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="actualSavings" 
                  name="Actual Savings" 
                  stroke="#059669" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorActualSavings)" 
                  connectNulls={false}
                />
              </>
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
